import React, { useState, useEffect } from 'react';
import { Row, Col, Card, Skeleton } from 'antd';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useRelatedCampaigns } from '../../../services/CampaignService';
import { Campaign } from 'models/CommonType';
import { truncateString } from 'utils/helpers';
import './RelatedCampaigns.css';

interface RelatedCampaignsProps {
  userId: string;
  authKey: string;
  currentCampaignId: string;
  isMobile?: boolean;
}

const RelatedCampaigns: React.FC<RelatedCampaignsProps> = ({
  userId,
  authKey,
  currentCampaignId,
  isMobile = false
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data: campaigns, isLoading } = useRelatedCampaigns(userId, authKey);

  // Filter out current campaign
  const relatedCampaigns = campaigns?.filter((campaign: Campaign) => campaign.id !== currentCampaignId) || [];

  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  
  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  const isMobileView = windowWidth <= 768;

  const handleCampaignClick = (campaignId: string) => {
    navigate(`/campaign?id=${campaignId}`);
  };

  if (isLoading) {
    if (isMobileView) {
      return (
        <div className="related-campaigns-mobile">
          {[1, 2, 3].map((item) => (
            <div key={item} className="search-campaign-item">
              <div className="search-campaign-image">
                <Skeleton.Image style={{ width: '80px', height: '80px', display: 'flex', justifyContent: 'center', alignItems: 'center' }} />
              </div>
              <div className="search-campaign-content">
                <Skeleton active paragraph={{ rows: 2 }} title={{ width: '80%' }} />
              </div>
            </div>
          ))}
        </div>
      );
    }
    
    return (
      <div className="related-campaigns">
        <Row gutter={[16, 16]}>
          {[1, 2, 3].map((item) => (
            <Col key={item} xs={24} sm={24} md={8} lg={8} xl={8}>
              <Card
                cover={<Skeleton.Image style={{ width: '100%', height: 200 }} />}
                className="related-campaign-card"
              >
                <Skeleton active paragraph={{ rows: 2 }} />
              </Card>
            </Col>
          ))}
        </Row>
      </div>
    );
  }

  if (!relatedCampaigns || relatedCampaigns.length === 0) {
    return (
      <div className="related-campaigns-empty">
        <p>{t('#Campaign.no_related_campaigns')}</p>
      </div>
    );
  }

  // Mobile view - search-like layout
  if (isMobileView) {
    return (
      <div className="related-campaigns-mobile">
        {relatedCampaigns.slice(0, 3).map((campaign: Campaign) => (
          <div 
            key={campaign.id} 
            className="search-campaign-item"
            onClick={() => handleCampaignClick(campaign.id)}
          >
            <div className="search-campaign-image">
              {campaign.image_small_url && (
                <img
                  alt={campaign.name}
                  src={campaign.image_small_url}
                  onError={(e) => {
                    (e.target as HTMLImageElement).style.display = 'none';
                  }}
                />
              )}
            </div>
            <div className="search-campaign-content">
              <div className="search-campaign-title">
                {truncateString(campaign.name, 60)}
              </div>
              <div className="search-campaign-description">
                {truncateString(campaign.description || '', 80)}
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  }
  
  // Desktop view - home page card layout
  return (
    <div className="related-campaigns">
      <Row gutter={[16, 16]}>
        {relatedCampaigns.slice(0, 3).map((campaign: Campaign) => (
          <Col key={campaign.id} xs={24} sm={24} md={8} lg={8} xl={8}>
            <Card
              hoverable
              cover={
                campaign.image_small_url && (
                  <div className="related-campaign-image">
                    <img
                      alt={campaign.name}
                      src={campaign.image_small_url}
                      onError={(e) => {
                        (e.target as HTMLImageElement).style.display = 'none';
                      }}
                    />
                  </div>
                )
              }
              className="related-campaign-card"
              onClick={() => handleCampaignClick(campaign.id)}
            >
              <Card.Meta
                title={
                  <div className="related-campaign-title">
                    {campaign.name}
                  </div>
                }
                description={
                  <div className="related-campaign-description">
                    {campaign.description?.substring(0, 100)}
                    {campaign.description?.length > 100 ? '...' : ''}
                  </div>
                }
              />
            </Card>
          </Col>
        ))}
      </Row>
    </div>
  );
};

export default RelatedCampaigns;